import React, { useState, useCallback } from 'react';
import block from 'propmods';
import { TextInput, Button } from 'lego-on-react';
import {
    GrammarIntentTestResult,
    GrammarValidationErrorPayload,
    GrammarIntentEditableFields,
} from '../../model/grammar';
import { GranetCodeEditor } from '../GranetCodeEditor/GranetCodeEditor';
import { ApiError } from '../../api';
import { ActionButton } from '../ActionButton/ActionButton';
import { GrammarPageTestResult } from '../GrammarPageTestResult/GrammarPageTestResult';
import { grammarSaveReducer, GrammarSaveAction } from '../../reducers/tooltips/grammarSaveReducer';
import { getBaseTooltipReducer } from '../../reducers/tooltips';
import { Hint } from '../Hint/Hint';
import Link from '../Link/Link';
import { CrossIcon } from '../../icons/cross';
import { getGranetCompletionProvider } from '../../lib/monaco/languages/granet/completion';
import { FormError } from '../Form/FormError';
import { routes } from '../../lib/routes';
import TextArea from '../Form/TextArea';
import * as keyset from 'i18n/IntentsPage';
import i18n from '@yandex-int/i18n';
import './GrammarPageIntentSettings.scss';
import { wrapInText } from '../../lib/i18n-utils';

const b = block('GrammarPageIntentSettings');
const t = i18n(keyset);

interface GrammarPageIntentSettingsFieldProps {
    title: string;
    hint?: React.ReactNode;
    required?: boolean;
    error?: string;
}

const GrammarPageIntentSettingsField: React.FC<GrammarPageIntentSettingsFieldProps> = ({
    children,
    title,
    required,
    hint,
    error,
}) => (
    <div {...b('field')}>
        <div {...b('title-wrapper')}>
            <h4 {...b('title', { required })}>{title}</h4>
            {Boolean(hint) && (
                <span {...b('hint')}>
                    <Hint hint={hint} />
                </span>
            )}
        </div>
        {children}
        {Boolean(error) && <FormError offsetTop>{error}</FormError>}
    </div>
);

type FetchingHandlerState = 'loading' | 'error' | 'idle';

interface Props {
    readOnly?: boolean;
    onClose: () => void;
    customEntitiesNames: string[];
    onShow: () => Promise<void>;
    onTestHandler: () => Promise<void>;
    onSaveHandler: () => Promise<void>;
    onDeleteHandler: () => Promise<void>;
    saveState: FetchingHandlerState;
    testState: FetchingHandlerState;
    deleteState: FetchingHandlerState;
    setValidationError: React.Dispatch<React.SetStateAction<GrammarValidationErrorPayload | null>>;
    validationError: GrammarValidationErrorPayload | null;
    testResults: GrammarIntentTestResult | undefined;
    onChange: <K extends keyof GrammarIntentEditableFields>(key: K) => (value: GrammarIntentEditableFields[K]) => void;
    hasErrors: boolean;
    errors: Partial<Record<keyof GrammarIntentEditableFields, string>>;
    settings: GrammarIntentEditableFields;
}

export const GrammarPageIntentSettings: React.FC<Props> = ({
    readOnly,
    onClose,
    customEntitiesNames,
    onShow,
    onTestHandler,
    onDeleteHandler,
    onSaveHandler,
    saveState,
    testState,
    deleteState,
    setValidationError,
    validationError,
    testResults,
    onChange,
    hasErrors,
    errors,
    settings,
}) => {
    // customEntitiesNames не меняются в пределах этого компонента
    const [completionProvider] = useState(() => getGranetCompletionProvider(customEntitiesNames));

    const isSaving = saveState === 'loading';
    const isTesting = testState === 'loading';
    const isDeleting = deleteState === 'loading';
    const isLoading = isSaving || isTesting || isDeleting;

    const hasAllRequiredSettings = Boolean(settings.formName) && Boolean(settings.humanReadableName);
    const isTestingDisabled =
        isLoading || readOnly || (!settings.negativeTests && !settings.positiveTests) || !hasAllRequiredSettings;

    const onSaveButtonClick = useCallback(async (): Promise<GrammarSaveAction | void> => {
        if (settings.sourceText.split('\n').length > 500) {
            return 'grammar-too-long';
        }

        if (!hasAllRequiredSettings) {
            return 'empty-required';
        }

        try {
            await onSaveHandler();
        } catch (e) {
            if (e instanceof ApiError && e.isDuplicateIntentIdError()) {
                return 'duplicate-id';
            }

            if (e instanceof ApiError && e.payload?.errorCode === 'GRAMMAR_SIZE_LIMIT') {
                return 'size-exceed';
            }

            throw e;
        }
    }, [onSaveHandler, settings.sourceText, settings.formName]);

    const onCodeChange = (code: string) => {
        onChange('sourceText')(code);
        setValidationError(null);
    };
    return (
        <div {...b()}>
            <div {...b('close')} onClick={onClose}>
                <CrossIcon color="#979797" />
            </div>
            <h2 {...b('heading')}>{t('header-settings')}</h2>
            <main {...b('main')}>
                <div {...b('column')}>
                    <GrammarPageIntentSettingsField title={t('title-name')} hint={t('hint-name')} required>
                        <TextInput
                            theme="normal"
                            size="s"
                            text={settings.humanReadableName}
                            onChange={onChange('humanReadableName')}
                            disabled={readOnly}
                        />
                    </GrammarPageIntentSettingsField>
                    <GrammarPageIntentSettingsField title="ID" required hint={t('hint-id')} error={errors.formName}>
                        <TextInput
                            theme="normal"
                            size="s"
                            text={settings.formName}
                            onChange={onChange('formName')}
                            disabled={readOnly}
                        />
                    </GrammarPageIntentSettingsField>
                    <h4 {...b('title')}>
                        {t('title-grammar')}
                        <span {...b('hint')}>
                            <Hint
                                hint={
                                    <>
                                        {wrapInText((s) => (
                                            <Link to={routes.documentation.aliceSkills.nlu} external target="_blank">
                                                {s}
                                            </Link>
                                        ))(t('hint-grammar'))}
                                    </>
                                }
                            />
                        </span>
                    </h4>
                    <div {...b('editor')}>
                        <GranetCodeEditor
                            code={settings.sourceText}
                            onChange={onCodeChange}
                            readOnly={readOnly || isLoading}
                            validationError={validationError || undefined}
                            onSave={onSaveButtonClick}
                            completionProvider={completionProvider}
                        />
                    </div>
                </div>
                <div {...b('column')}>
                    <GrammarPageIntentSettingsField title={t('title-positive-tests')} hint={t('hint-positive-tests')}>
                        <TextArea
                            onChange={readOnly ? () => undefined : onChange('positiveTests')}
                            rows={7}
                            text={settings.positiveTests}
                            autosized={false}
                        />
                    </GrammarPageIntentSettingsField>
                    <GrammarPageIntentSettingsField title={t('title-negative-tests')} hint={t('hint-negative-tests')}>
                        <TextArea
                            onChange={readOnly ? () => undefined : onChange('negativeTests')}
                            rows={7}
                            text={settings.negativeTests}
                            autosized={false}
                        />
                    </GrammarPageIntentSettingsField>
                    <GrammarPageIntentSettingsField title={t('title-result')}>
                        <div {...b('test-results')}>
                            {testResults ? <GrammarPageTestResult results={testResults} /> : null}
                        </div>
                    </GrammarPageIntentSettingsField>
                </div>
            </main>
            <div {...b('buttons')}>
                <div {...b('button-group')}>
                    <div {...b('button-save')}>
                        <ActionButton
                            tooltipReducer={grammarSaveReducer}
                            action={onSaveButtonClick}
                            defaultTooltipState={{ tooltipVisible: false }}
                            disabled={isTesting || isDeleting || isSaving || readOnly || hasErrors}
                            tooltipTo={['bottom', 'top']}
                        >
                            {t('button-save')}
                        </ActionButton>
                    </div>
                    <ActionButton
                        tooltipReducer={getBaseTooltipReducer({ successLabel: t('tooltip-tested') })}
                        action={onTestHandler}
                        theme="normal"
                        disabled={isTestingDisabled}
                        defaultTooltipState={{ tooltipVisible: false }}
                    >
                        {t('button-test')}
                    </ActionButton>
                    <Button
                        onClick={onShow}
                        theme={'pseudo'}
                        size={'s'}
                        pin={'round-round'}
                        cls={b('button-verstehen').className}
                    >
                        {t('button-get-requests')}&nbsp;
                        <span {...b('beta')} />
                    </Button>
                </div>
                <div {...b('button-group')}>
                    <ActionButton
                        tooltipReducer={getBaseTooltipReducer({ successLabel: '' })}
                        action={onDeleteHandler}
                        disabled={isLoading || readOnly}
                        defaultTooltipState={{ tooltipVisible: false }}
                        theme="normal"
                    >
                        {t('button-delete')}
                    </ActionButton>
                </div>
            </div>
        </div>
    );
};
